import { defineStore } from "pinia";
import { ref, Ref } from "vue";
import {
  IKpiLeaderboard,
  IKpiMonthlyFilter,
  IKpiShift,
  IKpiSummary,
  IKpiTeam,
  IKpiWorker,
  IKpiWorkType,
} from "@/features/kpi/kpi.types";
import { kpiService } from "@/features/kpi/kpi.service";
import { useNotification } from "@/utils/notification";
import { INewTask } from "@/features/tasks/task.types";
import { taskService } from "@/features/tasks/task.service";

const { pushNotification } = useNotification();

export const useKpiStore = defineStore("kpi", () => {
  async function getSummary(loading: Ref<boolean>): Promise<IKpiSummary> {
    try {
      loading.value = true;
      const { data } = await kpiService.getSummary();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function getWorkers(loading: Ref<boolean>): Promise<IKpiWorker[]> {
    try {
      loading.value = true;
      const { data } = await kpiService.getWorkers();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function getShifts(loading: Ref<boolean>): Promise<IKpiShift[]> {
    try {
      loading.value = true;
      const { data } = await kpiService.getShifts();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function getWorkTypes(loading: Ref<boolean>): Promise<IKpiWorkType[]> {
    try {
      loading.value = true;
      const { data } = await kpiService.getWorkTypes();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function getTeams(loading: Ref<boolean>): Promise<IKpiTeam[]> {
    try {
      loading.value = true;
      const { data } = await kpiService.getTeams();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function getLeaderboard(
    loading: Ref<boolean>
  ): Promise<IKpiLeaderboard[]> {
    try {
      loading.value = true;
      const { data } = await kpiService.getLeaderboard();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function getMonthlyStatistics(
    loading: Ref<boolean>,
    filter: IKpiMonthlyFilter,
    cl: any
  ) {
    loading.value = true;
    await kpiService
      .getMonthlyStatistics(filter)
      .then((res) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  return {
    getSummary,
    getWorkers,
    getShifts,
    getWorkTypes,
    getTeams,
    getLeaderboard,
    getMonthlyStatistics,
  };
});
